import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { schemas } from '@recurrency/core-api-schema';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import moment from 'moment';

import { Button } from 'components/Button';
import { Container } from 'components/Container';
import { DividerLine } from 'components/DividerLine';
import { FlexSpace } from 'components/FlexSpace';
import { ExternalLink, NotificationLink } from 'components/Links';
import { PageHeader } from 'components/PageHeader/PageHeader';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { captureError, HttpError } from 'utils/error';
import { splitIdNameStr } from 'utils/formatting';
import { LocalStorageKey, removeLocalStorageItem, useLocalStorage } from 'utils/localStorage';
import { useHashStateWithLocalStorage, routes } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { DailyWorkflowTrackEventSource, track, TrackEvent } from 'utils/track';

import { SearchFrameHashState } from 'types/hash-state';

import { DailyWorkflowContent } from './DailyWorkflowContent';
import { DailyWorkflowEmptyState } from './DailyWorkflowEmptyState';
import { DailyWorkflowFilters } from './DailyWorkflowFilters';
import { DailyWorkflowFinishedState } from './DailyWorkflowFinishedState';
import { DailyWorkflowSidePane } from './DailyWorkflowSidePane';
import {
  DailyWorkflowSubmissionConfirmationModal,
  DailyWorkflowSubmissionConfirmationResults,
} from './DailyWorkflowSubmissionConfirmation';
import { DailyWorkflowPageState } from './types';
import { getWorkflowReviewProgress } from './utils';

const MAX_DAILY_WORKFLOW_RUNS = 10;

export function DailyWorkflowScreen() {
  const { activeTenant } = useGlobalApp();

  const { hashState, updateHashState } = useHashStateWithLocalStorage<SearchFrameHashState>(
    LocalStorageKey.DailyWorkflowState,
  );

  const [lastDwRefresh, setDailyWorkflowLoadMore] = useLocalStorage(LocalStorageKey.DailyWorkflowLoadMore, undefined);

  const [pageState, setPageState] = useState<DailyWorkflowPageState>({
    selectedRuleId: '',
    selectedIndex: 0,
    resolutions: {},
    gutChecks: {},
  });

  const filters = useMemo(
    () => ({
      buyers: hashState.where?.buyer?.map((buyer) => splitIdNameStr(buyer).foreignId) || [],
      suppliers: hashState.where?.primary_supplier?.map((supplier) => splitIdNameStr(supplier).foreignId) || [],
      locations: hashState.where?.location?.map((location) => splitIdNameStr(location).foreignId) || [],
    }),
    [hashState],
  );

  /*
   * Search Validation Rules data and calculations
   */
  const {
    data: dailyWorkflowTasksData,
    reload: reloadTasks,
    setData: setDailyWorkflowTasksData,
    isReloading: isDailyWorkflowTasksReloading,
    isLoading: isDailyWorkflowTasksLoading,
  } = useCoreApi(schemas.dailyWorkflow.getDailyWorkflowTasks, {
    queryParams: {
      filter: filters,
    },
  });
  useEffect(() => {
    // When filters are chosen, rules can switch order so reset the index
    setPageState((prev) => ({
      ...prev,
      selectedRuleId: '',
      selectedIndex: 0,
    }));
  }, [filters, lastDwRefresh]);

  useEffect(() => {
    if (lastDwRefresh && Date.now() > moment(lastDwRefresh).valueOf()) {
      removeLocalStorageItem(LocalStorageKey.DailyWorkflowLoadMore);
    }
  }, [lastDwRefresh, setDailyWorkflowLoadMore]);

  /**
   * Set default rule if:
   * - no rule is selected
   * - current selected rule has no items (might happen when filters are changed)
   */
  useEffect(() => {
    const hasRules = dailyWorkflowTasksData && dailyWorkflowTasksData.items.length > 0;
    const selectedRule = dailyWorkflowTasksData?.items.find((r) => r.ruleId === pageState.selectedRuleId);

    if (hasRules && (pageState.selectedRuleId === '' || selectedRule?.taskCount === 0)) {
      setPageState((prev) => ({
        ...prev,
        selectedRuleId: dailyWorkflowTasksData.items[0].ruleId,
      }));
    }
  }, [dailyWorkflowTasksData, pageState.selectedRuleId, setPageState]);

  /*
   * Search Planning data and calculations
   */
  const {
    data: dailyWorkflowResponse,
    isLoading: isDailyWorkflowLoading,
    reload: reloadPlanning,
    isReloading: isPlanningReloading,
    setData: setPlanningData,
  } = useCoreApi(
    schemas.dailyWorkflow.getDailyWorkflowItemLocationData,
    pageState.selectedRuleId
      ? {
          pathParams: {
            ruleId: pageState.selectedRuleId,
          },
          queryParams: {
            filter: filters,
          },
        }
      : null,
  );
  const isLoadingOrRefreshing =
    isDailyWorkflowTasksLoading ||
    isDailyWorkflowLoading ||
    isDailyWorkflowTasksReloading ||
    isPlanningReloading ||
    lastDwRefresh !== undefined;

  const ruleIdx = useMemo(
    () => dailyWorkflowTasksData?.items.findIndex((item) => item.ruleId === pageState.selectedRuleId) ?? -1,
    [dailyWorkflowTasksData, pageState.selectedRuleId],
  );

  /**
   * Review Progress calculations
   */

  // Calculate review progress directly using utility function
  const reviewProgress = useMemo(() => {
    if (!dailyWorkflowTasksData) {
      return {
        totalItemsCount: 0,
        reviewedItemsCount: 0,
        submittedItemsCount: 0,
        areAllItemsReviewed: false,
        areAllItemsSubmitted: false,
        allReviewedItems: [],
        newItemsReviewedInSessionByRule: {},
        isDailyWorkflowTasksEmpty: true,
        remainingCount: 0,
        items: {},
      };
    }
    return getWorkflowReviewProgress(dailyWorkflowTasksData, pageState.resolutions);
  }, [dailyWorkflowTasksData, pageState.resolutions]);

  const hasNoRules = dailyWorkflowTasksData?.items.length === 0;
  const hasNoItems =
    dailyWorkflowTasksData?.items.reduce((acc: number, curr) => acc + curr.taskCount, 0) === 0 &&
    reviewProgress.submittedItemsCount === 0;

  const isEmptyWorkflowData = !isLoadingOrRefreshing && !lastDwRefresh && hasNoItems;

  // Handle submit
  const onPressSendReviews = async () => {
    const result = await showAsyncModal(DailyWorkflowSubmissionConfirmationModal, {});
    if (result?.submitted) {
      handleSubmitDailyWorkflow(result);
    }
  };

  const handleSubmitDailyWorkflow = async (result: DailyWorkflowSubmissionConfirmationResults) => {
    const submitNotification = createSubmissionNotification({
      entityName: 'Daily Workflow',
      erpType: activeTenant?.erpType,
      expectedWaitSeconds: 30,
      submittingMessage: 'Submitting daily workflow tasks...',
      fireInProgressNotification: true,
    });
    try {
      await coreApiFetch(schemas.dailyWorkflow.submitDailyWorkflowTasks, {
        pathParams: { tenantId: activeTenant?.id || '' },
        bodyParams: { bulkExcludeUntil: result.bulkExcludeUntil },
      });

      submitNotification.success({
        successMessage: 'Daily workflow tasks submitted',
        description: () => 'Daily workflow tasks submitted',
      });

      track(TrackEvent.DailyWorkflow_SubmitDailyWorkflowTasks, {
        totalItems: result.totalItems,
        acceptedItems: result.accepted,
        excludedItems: result.overridden,
        flaggedItems: result.flagged,
        source: DailyWorkflowTrackEventSource.DailyWorkflow,
      });

      (result.flaggedItems || []).forEach((flaggedRecord) => {
        track(TrackEvent.DemandPlanning_Record_Flag, {
          itemUid: flaggedRecord.itemId,
          itemId: flaggedRecord.itemCode || '',
          itemName: flaggedRecord.itemName || '',
          locationId: flaggedRecord.locationId,
          locationName: flaggedRecord.locationName || '',
          demandPredictability: '',
          demandPattern: '',
          flagged: true,
          flagReason: flaggedRecord.flagReason || '',
          recordUrl:
            window.location.origin +
            routes.demandPlanning.planning({
              where: {
                location: [`${flaggedRecord.locationId}: ${flaggedRecord.locationName || ''}`],
              },
              query: flaggedRecord.itemCode || '',
            }),
        });
      });

      // Wait for both reloads to complete before clearing state to prevent race conditions
      await Promise.all([reloadTasks(), reloadPlanning()]);

      // Clear resolutions after successful submission to prevent double counting
      setPageState((prev) => ({
        ...prev,
        resolutions: {},
        gutChecks: {},
      }));
    } catch (err) {
      submitNotification.error(err);
      captureError(err);
    }
  };

  const reloadDailyWorkflowTasks = useCallback(async () => {
    const updatedTaskData = await coreApiFetch(schemas.dailyWorkflow.getDailyWorkflowTasks, {
      queryParams: {
        filter: filters,
      },
    });
    setDailyWorkflowTasksData(updatedTaskData.data);
    setPageState((prev) => ({
      ...prev,
      selectedRuleId: '',
      selectedIndex: 0,
    }));
    const planningData = await coreApiFetch(schemas.dailyWorkflow.getDailyWorkflowItemLocationData, {
      pathParams: {
        ruleId: pageState.selectedRuleId,
      },
      queryParams: {
        filter: filters,
      },
    });
    setPlanningData(planningData.data);
    removeLocalStorageItem(LocalStorageKey.DailyWorkflowLoadMore);
  }, [filters, setDailyWorkflowTasksData, pageState.selectedRuleId, setPlanningData]);

  // Load more logic
  const loadMoreEnabled = useMemo(
    () => (dailyWorkflowTasksData?.dailyWorkflowRunIds?.length ?? 0) < MAX_DAILY_WORKFLOW_RUNS && !lastDwRefresh,
    [dailyWorkflowTasksData?.dailyWorkflowRunIds?.length, lastDwRefresh],
  );

  const loadMoreText = useMemo(() => {
    if (isDailyWorkflowTasksLoading || lastDwRefresh) {
      return 'Loading more items...';
    }
    return 'Load More Daily Workflow Items';
  }, [isDailyWorkflowTasksLoading, lastDwRefresh]);

  const fetchAdditionalDwItems = async () => {
    if (!loadMoreEnabled) return;

    const submitNotification = createSubmissionNotification({
      entityName: 'Load More Daily Workflow',
      expectedWaitSeconds: 60,
      submittingMessage: 'Loading more daily workflow items...',
      fireInProgressNotification: true,
    });
    setDailyWorkflowLoadMore(moment(submitNotification.notificationStartMs).add(5, 'minutes').toISOString());
    try {
      const { data: fetchAdditionalDailyWorkflowResult } = await coreApiFetch(
        schemas.dailyWorkflow.createAdditionalDailyWorkflowTasks,
        {
          pathParams: { tenantId: activeTenant?.id || '' },
        },
      );
      if (fetchAdditionalDailyWorkflowResult?.success) {
        submitNotification.success({
          description: (notificationKey) => (
            <NotificationLink notificationKey={notificationKey} to={routes.demandPlanning.dailyWorkflow()}>
              View new daily workflow recommendations
            </NotificationLink>
          ),
        });
      } else {
        submitNotification.error(
          fetchAdditionalDailyWorkflowResult?.message ||
            'No additional items flagged by rules were found. Please come back tomorrow for new daily workflow items.',
        );
      }
    } catch (err) {
      if (err && err instanceof HttpError && err.message.includes('No new items')) {
        submitNotification.success({
          successMessage:
            'No additional items flagged by rules were found. Please come back tomorrow for new daily workflow items.',
        });
      } else {
        captureError(err);
        submitNotification.error(
          'We are experiencing issues loading new daily workflow tasks. Please come back tomorrow for a new batch or try again later.',
        );
      }
    } finally {
      await reloadDailyWorkflowTasks();
    }
  };

  useEffect(() => {
    if (!dailyWorkflowResponse || !dailyWorkflowTasksData || isDailyWorkflowTasksLoading || isDailyWorkflowLoading)
      return;
    // If there are no unsubmitted items in this rule, move to the next rule
    if (dailyWorkflowResponse.items.length === 0) {
      // This is a circular dependency, but it's okay because it stops once there is data, and when it hits the end of the list of rules
      if (ruleIdx >= 0 && ruleIdx + 1 < dailyWorkflowTasksData.items.length && !reviewProgress.areAllItemsReviewed) {
        setPageState((prev) => ({
          ...prev,
          selectedRuleId: dailyWorkflowTasksData.items[ruleIdx + 1].ruleId,
        }));
      }
    }
  }, [
    dailyWorkflowResponse,
    dailyWorkflowTasksData,
    pageState.selectedRuleId,
    setPageState,
    isDailyWorkflowTasksLoading,
    isDailyWorkflowLoading,
    ruleIdx,
    reviewProgress.areAllItemsReviewed,
  ]);

  return (
    <Container>
      <PageHeader
        title="Daily Workflow"
        subtitle={
          <>
            Items refresh nightly – submit item updates today to save your work. Learn more{' '}
            <ExternalLink to="https://help.recurrency.com/en/articles/12020624-daily-workflow">here</ExternalLink>.
          </>
        }
        headerActions={
          <Button
            type="primary"
            permissionsRequired={[
              TenantUserPermission.ActionExportMinMax,
              TenantUserPermission.ActionEditItemSupplierDetails,
            ]}
            disabled={
              isDailyWorkflowLoading ||
              isDailyWorkflowTasksLoading ||
              isEmptyWorkflowData ||
              lastDwRefresh !== undefined
            }
            onClick={onPressSendReviews}
          >
            Send Reviews to {activeTenant?.erpType.toUpperCase()}
          </Button>
        }
      />
      <DividerLine />
      <FlexSpace direction="row" gap={20} alignItems="flex-start">
        {!hasNoRules && (
          <DailyWorkflowSidePane
            dailyWorkflowTasks={dailyWorkflowTasksData?.items || []}
            pageState={pageState}
            reviewProgress={reviewProgress}
            setSelectedRuleId={(ruleId) => {
              setPageState((prev) => ({
                ...prev,
                selectedRuleId: ruleId,
              }));
            }}
            setSelectedIndex={(index) => {
              setPageState((prev) => ({
                ...prev,
                selectedIndex: index,
              }));
            }}
            filters={<DailyWorkflowFilters hashState={hashState} updateHashState={updateHashState} />}
          />
        )}

        {isEmptyWorkflowData ? (
          <DailyWorkflowEmptyState hasNoRules={hasNoRules} />
        ) : reviewProgress.areAllItemsReviewed && lastDwRefresh === undefined && !isLoadingOrRefreshing ? (
          <DailyWorkflowFinishedState
            type={reviewProgress.areAllItemsSubmitted ? 'submitted' : 'reviewed'}
            onPressSendReviews={onPressSendReviews}
            secondaryButton={
              <Button type="primary" disabled={!loadMoreEnabled} onClick={fetchAdditionalDwItems}>
                {loadMoreText}
              </Button>
            }
          />
        ) : (
          <DailyWorkflowContent
            ruleIdx={ruleIdx}
            pageState={pageState}
            setPageState={setPageState}
            dailyWorkflowResponse={dailyWorkflowResponse}
            isDailyWorkflowLoading={isLoadingOrRefreshing}
            reloadPlanning={reloadPlanning}
            dailyWorkflowTasksData={dailyWorkflowTasksData}
            onAccept={(bodyParams) => coreApiFetch(schemas.dailyWorkflow.updateDailyWorkflowTask, { bodyParams })}
            onReset={async (queryParams) => {
              const res = await coreApiFetch(schemas.dailyWorkflow.deleteDailyWorkflowTaskResolution, {
                queryParams,
              });
              reloadTasks();
              reloadPlanning();
              return res;
            }}
            onOverrideAndExclude={(bodyParams) =>
              coreApiFetch(schemas.dailyWorkflow.updateDailyWorkflowTask, { bodyParams })
            }
            onFlag={(bodyParams) =>
              coreApiFetch(schemas.dailyWorkflow.updateDailyWorkflowTask, {
                bodyParams,
              })
            }
            trackEventSource={DailyWorkflowTrackEventSource.DailyWorkflow}
          />
        )}
      </FlexSpace>
    </Container>
  );
}
